import {objectFromEntries} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import DataFrameTablePreview from 'analysis-tool/data-frame-creation/DataFrameTablePreview';
import {DataFrame, DataFrameColumnDef, DataFrameRow} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreviewOutput} from 'pipelines/network/PipelineDAO';
import {FIELD_TYPE_TO_LEAF_TYPE} from 'schemas/fields/SchemaFieldConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

import {DataMappingState, TargetSpecificMappingState} from './DataMappingTypes';
import {REQUIRED_COLUMN_DEFS_BY_TARGET} from './targetSpecificColumns';

export interface SaveToResultsStepOutputPreviewProps {
  clientStateId: string;
  inputDataFrame: DataFrame;
  dataMappingState: DataMappingState;
  targetSpecificMappingState: TargetSpecificMappingState | null;
}

/**
 * This component renders a preview of the output of the SaveToResults step using its input configs
 *
 * Known limitation: this component generates the preview purely on the frontend
 * and cannot replicate all the logic especially reading data frame csv back into results.
 * This preview cannot detect and render the "CORRECT" preview if "Rows with errors" is expected for now.
 */
export default function SaveToResultsStepOutputPreview({
  clientStateId,
  inputDataFrame,
  dataMappingState,
  targetSpecificMappingState,
}: SaveToResultsStepOutputPreviewProps): JSX.Element {
  if (
    (dataMappingState.resultSchema == null || dataMappingState.mappings.length === 0) &&
    (targetSpecificMappingState == null || targetSpecificMappingState.columnMappings.length === 0)
  ) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No rows to preview" />;
  }

  const previewDataFrame = generateResultTablePreview(
    inputDataFrame,
    dataMappingState,
    targetSpecificMappingState
  );

  const previewOutput: PreviewOutput<PipelineStepType.SAVE_TO_RESULTS, 1> = {
    dataFrames: {
      'Saved results': [previewDataFrame],
      'Rows with errors': [],
    },
    outputStepDatas: {
      'Saved results': [previewDataFrame],
      'Rows with errors': [],
    },
  };

  return (
    <div className={css(styles.previewContainer)}>
      <DataFrameTablePreview
        clientStateId={clientStateId}
        getDataFrameFromOutput={(previewOutput) => previewOutput.dataFrames['Saved results'][0]}
        previewState={{
          status: AsyncStatus.SUCCESS,
          data: previewOutput,
        }}
      />
    </div>
  );
}

function generateResultTablePreview(
  inputDataFrame: DataFrame,
  dataMappingState: DataMappingState,
  targetSpecificMappingState: TargetSpecificMappingState | null,
  maxRows: number = 10
): DataFrame {
  const previewName = 'Preview';
  const previewColumns: Array<DataFrameColumnDef> = [];
  const previewRows: Array<DataFrameRow> = [];

  const previewSystemNameToInputSystemName: Record<string, string> = {};

  // add result schema fields as columns
  const {resultSchema, mappings} = dataMappingState;
  const resultSchemaColumnMapping = objectFromEntries(
    mappings.map(({outputFieldId, inputColumnSystemName}) => [outputFieldId, inputColumnSystemName])
  );
  for (const field of resultSchema?.fields ?? []) {
    if (resultSchemaColumnMapping[field.apiId] == null) {
      continue;
    }
    const columnDef: DataFrameColumnDef = {
      systemName: field.apiId,
      displayName: field.displayName,
      type: {leafType: FIELD_TYPE_TO_LEAF_TYPE[field.fieldType]},
    };
    previewSystemNameToInputSystemName[field.apiId] = resultSchemaColumnMapping[field.apiId];
    previewColumns.push(columnDef);
  }

  // add target specific  columns
  if (targetSpecificMappingState != null) {
    const {target, columnMappings} = targetSpecificMappingState;
    const targetColumnDefBySystemName = objectFromEntries(
      REQUIRED_COLUMN_DEFS_BY_TARGET[target].map((colDef) => [colDef.systemName, colDef])
    );
    for (const {inputColumnSystemName, outputColumnSystemName} of columnMappings) {
      previewSystemNameToInputSystemName[outputColumnSystemName] = inputColumnSystemName;
      previewColumns.push(targetColumnDefBySystemName[outputColumnSystemName]);
    }
  }

  // fill rows, upto maxRows
  if (Object.keys(previewSystemNameToInputSystemName).length > 0) {
    for (let i = 0; i < Math.min(maxRows, inputDataFrame.rows.length); i++) {
      const inputRow = inputDataFrame.rows[i];
      const previewRow: DataFrameRow = {};
      for (const [previewSystemName, inputSystemName] of Object.entries(previewSystemNameToInputSystemName)) {
        previewRow[previewSystemName] = inputRow[inputSystemName];
      }
      previewRows.push(previewRow);
    }
  }

  const previewDataFrame: DataFrame = {
    name: previewName,
    columns: previewColumns,
    numRows: inputDataFrame.numRows,
    rows: previewRows,
    hydratedProperties: {
      apiIdentifiedObjects: {},
      blobDicts: {},
    },
  };

  return previewDataFrame;
}

const styles = StyleSheet.create({
  previewContainer: {
    marginTop: '8px',
  },
});
